<template>
  <div
    class="prorata-summary"
    :style="{ backgroundColor: badgeStyle.backgroundColor, border: '1px solid ' + badgeStyle.borderColor }"
    style="border-radius: 0.5rem; padding: 0.875rem 1rem; margin-bottom: 1.5rem;">
    <!-- Linha do plano -->
    <div style="display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-bottom: 0.5rem;">
      <span style="font-size: 0.875rem; color: #374151; min-width: 0;">
        <span style="font-weight: 600;">Plano {{ planDetails ? planDetails.name : '' }}</span><span v-if="planDetails && planDetails.minutesLabel && !hasBenefits" style="color: #6b7280;"> ({{ planDetails.minutesLabel }})</span>
      </span>
      <span style="font-size: 0.875rem; color: #374151; white-space: nowrap; flex-shrink: 0;">
        <span v-if="monthlyFee === null" style="color: #9ca3af;">&mdash;</span>
        <span v-else>{{ monthlyFee === 0 ? 'Grátis' : formatCurrency(monthlyFee) + '/mês' }}</span>
      </span>
    </div>
    <p v-if="hasBenefits" style="font-size: 0.75rem; color: #4b5563; margin: -0.25rem 0 0.5rem 0;">
      {{ planDetails.benefits.join(' · ') }}
    </p>
    <p v-if="planDetails && planDetails.campaignDiscount" style="margin: 0 0 0.5rem 0;">
      <campaign-offer-note :plan-name="planDetails.name" :discount="planDetails.campaignDiscount" />
    </p>
    <!-- Linha do proporcional -->
    <div style="display: flex; align-items: baseline; justify-content: space-between; gap: 1rem;">
      <span style="font-size: 0.8125rem; color: #6b7280; min-width: 0;">
        <span v-if="loading">Calculando proporcional...</span>
        <span v-else-if="error" style="color: #b45309;">{{ error }}</span>
        <span v-else-if="preview">Proporcional ({{ preview.daysInCycle }} dias até {{ formatDate(preview.cycleEnd) }})</span>
      </span>
      <span
        v-if="preview && !loading && !error"
        style="font-size: 0.8125rem; color: #374151; white-space: nowrap; flex-shrink: 0;">
        {{ formatCurrency(preview.proratedFee) }}
      </span>
    </div>
    <!-- Linha da mensalidade adiantada. Só aparece quando a cobrança do próximo ciclo
         já foi quitada: ela saiu sem este usuário e não há como corrigi-la, então a
         mensalidade dele vem junto aqui. Sem esta linha o total não bate com a única
         parcela listada acima e o cliente não tem como fechar a conta (TEL-2297). -->
    <div
      v-if="showNextCycleFee"
      style="display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-top: 0.25rem;">
      <span style="font-size: 0.8125rem; color: #6b7280; min-width: 0;">Mensalidade do próximo ciclo, já faturado</span>
      <span style="font-size: 0.8125rem; color: #374151; white-space: nowrap; flex-shrink: 0;">
        {{ formatCurrency(preview.nextCycleFee) }}
      </span>
    </div>
    <!-- Total a cobrar agora -->
    <div
      v-if="preview && !loading && !error"
      :style="{ borderTop: '1px solid ' + badgeStyle.borderColor }"
      style="display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin-top: 0.75rem; padding-top: 0.75rem;">
      <span style="font-size: 0.9375rem; font-weight: 700; color: #111827;">Total a cobrar agora</span>
      <span :style="{ fontSize: '1.125rem', fontWeight: 700, color: badgeStyle.color, whiteSpace: 'nowrap', flexShrink: 0 }">
        {{ formatCurrency(chargeTotal) }}
      </span>
    </div>
  </div>
</template>

<script>
import { formatCurrency } from '@/utils/currency'
import CampaignOfferNote from '../../Subscription/CampaignOfferNote.vue'

export default {
  name: 'ProRataSummary',
  components: {
    CampaignOfferNote
  },
  props: {
    // { name, minutesLabel, monthlyFee, benefits?, campaignDiscount? }
    planDetails: {
      type: Object,
      default: null
    },
    // { backgroundColor, color, borderColor } — paleta do plano (PlanBadge).
    badgeStyle: {
      type: Object,
      required: true
    },
    // { daysInCycle, cycleEnd, baseMonthlyFee, proratedFee, nextCycleFee,
    //   nextCycleAlreadyPaid, totalFee }
    //
    // `proratedFee` é SÓ a parcela dos dias restantes do ciclo. Quem manda no valor
    // cobrado é `totalFee` (TEL-2297).
    preview: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  computed: {
    hasBenefits () {
      return !!this.planDetails && Array.isArray(this.planDetails.benefits) && this.planDetails.benefits.length > 0
    },
    // Única fonte: `baseMonthlyFee` é o valor que o billing usou de fato, na tabela de
    // preços da CONTA. O catálogo (`planDetails`) é o preço de quem contrata hoje, e
    // deliberadamente NÃO serve de fallback aqui — ele já está montado quando o modal
    // abre, então usá-lo enquanto o preview não chega faz a caixa piscar o preço novo
    // e trocar pelo da conta. Numa tela de cobrança, meio segundo com o número errado
    // é pior que meio segundo sem número.
    //
    // `null` = ainda não dá para saber. O template mostra um traço, no mesmo espírito
    // do "Calculando proporcional..." da linha de baixo.
    monthlyFee () {
      if (this.preview && this.preview.baseMonthlyFee !== undefined && this.preview.baseMonthlyFee !== null) {
        return Number(this.preview.baseMonthlyFee)
      }
      return null
    },
    // O que a confirmação vai cobrar de verdade. Até o TEL-2297 isto era a própria
    // pró-rata, e a linha do total mostrava `proratedFee` sem errar. Depois que o
    // billing passou a adiantar a mensalidade do ciclo já quitado, os dois deixaram
    // de ser a mesma coisa — e esta linha continuava mostrando a parcela menor.
    //
    // O fallback para `proratedFee` deixa a ordem de deploy livre: contra um billing
    // que ainda não manda `totalFee`, a tela segue mostrando o que ele cobra.
    chargeTotal () {
      if (!this.preview) return 0
      if (this.preview.totalFee !== undefined && this.preview.totalFee !== null) {
        return Number(this.preview.totalFee)
      }
      return Number(this.preview.proratedFee)
    },
    // A parcela adiantada só é discriminada quando existe de fato. Conta normal —
    // a esmagadora maioria — não vê esta linha.
    showNextCycleFee () {
      return !!this.preview &&
        !this.loading &&
        !this.error &&
        this.preview.nextCycleAlreadyPaid === true &&
        Number(this.preview.nextCycleFee) > 0
    }
  },
  methods: {
    formatCurrency,
    formatDate (value) {
      if (!value || typeof value !== 'string') return ''
      const parts = value.slice(0, 10).split('-')
      if (parts.length !== 3) return ''
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Users/invite/ProRataSummary.vue